import { MuiButton } from '@/components';
import { UseFirstRender, UseIntermitence } from '@/hooks';
import {
  actualStory,
  createStories,
  getUploadSignedUrl,
  openSubscriptionPopup,
  setPendingStory,
  updateStory,
  updateStoryViewG,
} from '@/store/actions';
import { authSelector, currentStorySelector, intermitenceSelector, pendingStorySelector } from '@/store/selectors';
import {
  calculateFileSize,
  cdn_url,
  checkRoleAndPermission,
  FetchFileService,
  fileConverter,
  finalPayload,
  transformPayload,
} from '@/utils';
import { Box, IconButton, Theme, Typography, useMediaQuery } from '@mui/material';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CancelModal } from './components';
import { RefreshUserData } from '@/utils/fetchUserData';
import MuiTextField3 from '@/components/TextField3';
import { storyTemplates } from '../CreateStory/formsTemplates';
import ImageUploadBox from '@/components/ImageUpload';

export const EditStory: FC<any> = () => {
  const { t } = useTranslation();

  const { user } = useSelector(authSelector);
  const story = useSelector(currentStorySelector);
  const { separation } = useSelector(intermitenceSelector);
  const router = useRouter();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const [update, setUpdate] = useState(false);
  const [initialValues, setInitialValues] = useState<any>({});
  const [isDirty, setIsDirty] = useState(false);
  const pendingStory = useSelector(pendingStorySelector);
  const [coverImage, setCoverImage] = useState<string | null>(null);

  UseFirstRender(() => {
    if (!story?.id) {
      let pendStory = null;
      if (typeof window !== 'undefined') {
        const pstory = localStorage.getItem('pendingStory');
        pendStory = pstory ? JSON.parse(pstory) : null;
      }
      dispatch(setPendingStory(pendStory));
    } else {
      dispatch(actualStory(router.query?.id as string));
    }
  }, [router?.query?.id, story?.id]);

  const [values, setValues] = useState<any>({});
  const { status, switchStatus } = UseIntermitence();
  const [DefaultLoading, setDefaultLoading] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (user && story?.id) {
      dispatch(updateStoryViewG(user.id));
    }
  }, [user, dispatch]);

  UseFirstRender(() => {
    if (story?.id) {
      setDefault();

      return;
    }

    if (!story?.id && pendingStory) {
      setDefault();

      return;
    }
    setValues({});
  }, [story, pendingStory]);
  const setDefault = async () => {
    setDefaultLoading(true);
    try {
      const source = story?.id ? story : pendingStory;
      const storyType = source?.story_details?.type_of_story;
      const formValues = storyTemplates.find((t) => t.key === storyType);
      if (!formValues) return;

      const previewUrl = source?.cover_image ? `${cdn_url}${source.cover_image}` : null;

      const defaultValues = {
        title: source?.title ?? '',
        description: source?.description ?? '',
        story_title_image: {
          cover_image: null,
          preview: previewUrl,
          existingKey: source?.cover_image,
        },
        ...source?.story_details?.general_info,
      };

      setValues(defaultValues);
      setInitialValues(defaultValues);
    } catch (e) {
      console.error('Error setting default values:', e);
    } finally {
      setDefaultLoading(false);
    }
  };

  useEffect(() => {
    setIsDirty(JSON.stringify(values) !== JSON.stringify(initialValues));
  }, [values, initialValues]);

  const processFile = (prev_stories: any, updatedValues: any) => {
    const idSource = story?.id ? story : pendingStory;
    dispatch(
      getUploadSignedUrl(
        {
          file: `stories/${prev_stories?.title}/${prev_stories?.name_image}`,
          type: prev_stories?.type_image,
        },
        async (res: any) => {
          try {
            const response = await FetchFileService(
              res?.value?.url?.uploadUrl,
              'PUT',
              updatedValues.story_title_image?.cover_image,
              prev_stories?.type_image,
            );

            if (response?.ok) {
              const file = values.story_title_image?.cover_image;
              const fileSizeBytes = calculateFileSize(file);
              const valuesFinal: any = finalPayload(
                { ...prev_stories, fileSize: fileSizeBytes },
                idSource?.story_details?.type_of_story,
              );
              valuesFinal.id = idSource?.id;
              if (story?.id) {
                dispatch(updateStory({ valuesFinal, router }));
              } else {
                const file = values.story_title_image?.cover_image;
                const fileSizeBytes = calculateFileSize(file);
                const valuesFinal: any = finalPayload(
                  { ...prev_stories, fileSize: fileSizeBytes },
                  idSource?.story_details?.type_of_story,
                );
                localStorage.removeItem('pendingStory');
                dispatch(createStories({ valuesFinal, router, flag: true }));
              }
            }
          } catch (error) {
            console.error('File processing failed:', error);
          }
        },
      ),
    );
  };
  const handleRemoveImage = () => {
    setValues((prev: any) => ({
      ...prev,
      story_title_image: null,
    }));
  };
  const handleBackClick = () => {
    if (isDirty) {
      switchStatus();
    } else {
      router.back();
    }
  };

  const updateAction = useCallback(
    async (newValues: any) => {
      const idSource = story?.id ? story : pendingStory;
      const updatedValues = { ...values, ...newValues };

      const imgState = updatedValues?.story_title_image;
      const hasNewFile = imgState?.cover_image instanceof File;
      // Check if user explicitly removed the image (imgState is null, not undefined)
      const imageWasRemoved = updatedValues.story_title_image === null;

      const imageName = hasNewFile ? imgState.cover_image.name : null;
      const imageType = hasNewFile ? imgState.cover_image.type : null;

      const valuesCopy = transformPayload(
        updatedValues,
        idSource?.story_details?.type_of_story,
        user?.id,
        user?.email,
        imageName,
        imageType,
        idSource?.status,
      );

      const res = await RefreshUserData(user?.token, user?.id);
      const userData = res?.result;
      const subsperm = checkRoleAndPermission(
        userData?.roles,
        'Subscriber_Individual',
        'CLIENT_STORY_CREATE',
        user?.id,
      );

      if (!subsperm) {
        localStorage.setItem('router_history', `/app/story/${story?.url}`);
        dispatch(openSubscriptionPopup());
        return;
      }
      const file = imgState?.cover_image as File | null;
      const fileSizeBytes = file ? calculateFileSize(file) : 0;
      const prev_stories = { ...valuesCopy, fileSize: fileSizeBytes };
      if (hasNewFile && file) {
        processFile(prev_stories, updatedValues);
      } else {
        // If user removed image, set to null; otherwise use existing key or original
        const coverImageValue = imageWasRemoved
          ? null
          : (imgState?.existingKey ?? idSource?.cover_image ?? null);

        const valuesFinal: any = finalPayload(
          {
            ...valuesCopy,
            cover_image: coverImageValue,
            fileSize: fileSizeBytes,
          },
          idSource?.story_details?.type_of_story,
        );
        valuesFinal.id = idSource?.id;

        if (story?.id) {
          dispatch(updateStory({ valuesFinal, router }));
        } else {
          localStorage.removeItem('pendingStory');
          dispatch(createStories({ valuesFinal, router, flag: true }));
        }
      }
    },
    [values, story, pendingStory, user?.id, user?.email, user?.token, router, dispatch],
  );

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Process image through fileConverter to handle EXIF orientation
    const processedFile = file.type.startsWith('image/') ? await fileConverter(file) : file;
    const finalFile = processedFile || file;

    const imageUrl = URL.createObjectURL(finalFile);
    setValues((prev: any) => ({
      ...prev,
      story_title_image: {
        cover_image: finalFile,
        preview: imageUrl,
        existingKey: prev?.story_title_image?.existingKey ?? null,
      },
    }));
  };

  useEffect(
    () => () =>
      values?.story_title_image?.preview?.startsWith('blob:') && URL.revokeObjectURL(values.story_title_image.preview),
    [values?.story_title_image?.preview],
  );

  const updateButton = () => {
    setUpdate(true);
    updateAction(values);
  };
  const selectedStory =
    storyTemplates.find((item) => item.key == story?.story_details?.type_of_story) || storyTemplates[0];

  const useBodyBackground = (color: string = '#fff') => {
    useEffect(() => {
      document.body.style.background = color;

      return () => {
        document.body.style.background = '';
      };
    }, [color]);
  };
  useBodyBackground(selectedStory?.bgcolor);
  return (
    <>
      {!isMobile && (
        <Image
          src={`${selectedStory.icon}`}
          style={{ position: 'absolute', top: '47%', left: '30%' }}
          width={764}
          height={504}
          alt='icons'
        />
      )}
      <Box
        display={'flex'}
        width={'100%'}
        justifyContent={'flex-start'}
        flexDirection={'column'}
        height={isMobile ? 'auto' : '100%'}
        paddingTop={isMobile ? '0' : '6.175rem'}
        alignItems={isMobile ? 'flex-start' : 'center'}>
        <Box width={'100%'} zIndex={0} padding={isMobile ? '0 0 150px 0' : '0'} height={'auto'}>
          <Box
            display={'flex'}
            height={'100%'}
            justifyContent={'space-between'}
            marginBottom={isMobile ? '0' : '1rem'}
            alignItems={isMobile ? 'center' : 'flex-start'}
            width={'100%'}>
            <Box width={'100%'} zIndex={1}>
              <Box
                width='100%'
                height='86px'
                bgcolor={selectedStory.header_color}
                display='flex'
                alignItems='center'
                justifyContent='center'>
                <Box maxWidth='1200px' width='100%' padding='0 16px' display='flex' alignItems='center'>
                  <IconButton onClick={handleBackClick} sx={{ color: selectedStory.color, marginRight: '16px' }}>
                    <Image src={'/icons/left-arrow-black.svg'} width={20} height={20} alt='back' />
                  </IconButton>
                  <Typography variant='h4' color={selectedStory.color} sx={{ flexGrow: 1, textAlign: 'center' }}>
                    {t(story?.story_details?.type_of_story)}
                  </Typography>
                </Box>
              </Box>

              <Box
                borderRadius={'0.5rem'}
                zIndex={0}
                width={isMobile ? '100%' : '100%'}
                minHeight={isMobile ? '100%' : '37.75rem'}
                height={'100%'}
                padding={isMobile ? '0.5rem ' : '3.5rem'}
                position={'relative'}>
                <Box
                  display='flex'
                  flexDirection='column'
                  alignItems='center'
                  maxWidth='400px'
                  mx='auto'
                  p={3}
                  borderRadius={2}
                  width='100%'>
                  <Typography color='#212d6d' fontSize='1rem' textAlign='left' alignSelf='start' width='100%' mb={1}>
                    Name this story
                  </Typography>
                  <MuiTextField3
                    value={values?.title}
                    onChange={(e) => setValues((prev: any) => ({ ...prev, title: e.target.value }))}
                    fullWidth
                    placeholder='Whose life are you celebrating?'
                    variant='outlined'
                    sx={{ mb: 2, bgcolor: 'white' }}
                  />

                  <ImageUploadBox
                    handleUpload={handleImageUpload}
                    imageUrl={values?.story_title_image?.preview}
                    handleDelete={handleRemoveImage}
                  />

                  <Typography color='#212d6d' fontSize='1rem' textAlign='left' alignSelf='start' width='100%' mb={1}>
                    Description (Optional)
                  </Typography>
                  <MuiTextField3
                    value={values?.description}
                    onChange={(e) => setValues((prev: any) => ({ ...prev, description: e.target.value }))}
                    fullWidth
                    placeholder='A quick blurb celebrating why this person was so special.'
                    variant='outlined'
                    multiline
                    minRows={6}
                    sx={{ mb: 2, height: '150px', bgcolor: 'white' }}
                  />
                  <MuiButton
                    loading={update || DefaultLoading}
                    method={updateButton}
                    height='65px'
                    sx={{
                      backgroundColor: '#212d6d',
                      color: 'white',
                      gap: '1.6rem',
                      py: 2,
                      borderRadius: '8px',
                      '&:hover': { backgroundColor: '#0F1235' },
                    }}>
                    <Image src={'/icons/tick.svg'} width={13} height={9} alt='tick' />
                    <Typography fontSize={'0.9rem'} color='#F1F7FA'>
                      Update
                    </Typography>
                  </MuiButton>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      <CancelModal
        color={selectedStory?.color}
        open={status}
        onClose={switchStatus}
        confirmRoute={`/app/story/${story?.url}`}
      />
      {/* <DeleteStoryModal open={deleteStatusStory} onClose={switchDeleteStory} confirmMethod={deleteStoryAction} /> */}
    </>
  );
};
